import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPermissionOverrideModel, PermissionKey } from '@mesterplan/shared-types';
import { useFirebase } from '../context';
import { CACHE_KEYS, NotificationCallbacks, CompanyContext } from '../types';
import {
  flatMapToGrantedRevoked,
  grantedRevokedToFlatMap,
} from './permissionsApiBridge';

interface UseUserPermissionOverrideOperationsOptions {
  notifications?: NotificationCallbacks;
  selectedCompany?: CompanyContext | null;
}

/**
 * Hook providing CRUD operations for user permission overrides.
 *
 * Backed entirely by the .NET permissions controller
 * (`/api/permissions/users/{userId}`), which scopes the org from the session.
 *
 * @example
 * ```tsx
 * const {
 *   useFetchUserPermissionOverride,
 *   useSetUserPermissionOverride,
 *   useClearUserPermissionOverrides,
 * } = useUserPermissionOverrideOperations({
 *   selectedCompany: { id: company.id },
 * });
 *
 * // Fetch current user's overrides
 * const { data: myOverrides } = useFetchUserPermissionOverride(currentUser.uid);
 *
 * // Set a single permission override
 * const setOverride = useSetUserPermissionOverride();
 * setOverride.mutate({
 *   userId: 'user123',
 *   permission: 'quotes.view',
 *   value: true,
 * });
 * ```
 */
export function useUserPermissionOverrideOperations(
  options: UseUserPermissionOverrideOperationsOptions = {}
) {
  const queryClient = useQueryClient();
  const { api } = useFirebase();

  /**
   * Per-user overrides live only in the .NET aggregate now. The Firestore branch read and wrote
   * `COMPANIES/{id}/USER_PERMISSION_OVERRIDES/{userId}` and sat behind a `currentUser` gate that
   * is always null on the IDP path (`App.tsx` mounts `FirebaseProvider currentUser={firebaseUser}`
   * and `IdpGatedApp` passes `firebaseUser={null}`) — so every write bailed out before reaching
   * the API, showing the misleading "Ingen virksomhed valgt". A missing client here is a wiring
   * bug, not a legacy company.
   */
  const requireApi = () => {
    if (!api) {
      throw new Error('Brugertilladelser kræver .NET-klienten (ingen MesterplanApi monteret)');
    }
    return api;
  };
  const { notifications, selectedCompany } = options;

  /** Read the user's current override from .NET as a flat `{ key: boolean }` map (404 → {}). */
  const readNetOverrideFlat = async (
    userId: string,
  ): Promise<Partial<Record<PermissionKey, boolean>>> => {
    const res = await requireApi().permissions.getUserOverride(userId);
    if (!res.ok) {
      if (res.error?.status === 404) {
        return {};
      }
      throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke hente brugertilladelser');
    }
    return grantedRevokedToFlatMap(res.data.granted, res.data.revoked);
  };

  /**
   * Write the recomputed flat override map back to .NET: an empty map deletes the override
   * entirely (matching the Firestore "no permissions left → delete the doc" rule); otherwise
   * PUT the granted/revoked split.
   */
  const writeNetOverride = async (
    userId: string,
    flat: Partial<Record<PermissionKey, boolean>>,
  ): Promise<void> => {
    if (Object.keys(flat).length === 0) {
      const res = await requireApi().permissions.deleteUserOverride(userId);
      // A 404 here just means there was nothing to delete — treat as success.
      if (!res.ok && res.error?.status !== 404) {
        throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke fjerne brugertilladelser');
      }
      return;
    }

    const res = await requireApi().permissions.setUserOverride(userId, flatMapToGrantedRevoked(flat));
    if (!res.ok) {
      throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke opdatere brugertilladelse');
    }
  };

  /**
   * Fetch a single user's permission overrides.
   */
  const useFetchUserPermissionOverride = (userId?: string, companyId?: string) => {
    const targetCompanyId = companyId || selectedCompany?.id;

    return useQuery({
      queryKey: [CACHE_KEYS.USER_PERMISSION_OVERRIDE, targetCompanyId, userId],
      queryFn: async (): Promise<UserPermissionOverrideModel | null> => {
        if (!targetCompanyId || !userId) {
          return null;
        }

        // Read granted/revoked and rebuild the flat `permissions` map the SPA renders
        // (granted → true, revoked → false). A 404 means the user has no override.
        const res = await requireApi().permissions.getUserOverride(userId);
        if (!res.ok) {
          if (res.error?.status === 404) {
            return null;
          }
          console.error('Error fetching user permission override:', res.error);
          throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke hente brugertilladelser');
        }
        return {
          id: userId,
          permissions: grantedRevokedToFlatMap(res.data.granted, res.data.revoked),
          updatedAt: '',
          updatedBy: '',
        };
      },
      enabled: !!targetCompanyId && !!userId,
    });
  };

  /**
   * Set a single permission override for a user.
   * Creates the document if it doesn't exist, or merges with existing overrides.
   */
  const useSetUserPermissionOverride = () => {
    return useMutation({
      mutationFn: async (data: {
        userId: string;
        permission: PermissionKey;
        value: boolean | null; // null to remove the override
        companyId?: string;
      }): Promise<void> => {
        const targetCompanyId = data.companyId || selectedCompany?.id;

        if (!targetCompanyId) {
          notifications?.onError?.('Ingen virksomhed valgt');
          return;
        }

        // .NET path: read-modify-write the override. Read current granted/revoked, rebuild the
        // flat map, apply the single key change (null → remove the override entirely), and
        // either delete the override (empty) or PUT the recomputed granted/revoked split.
        // Mirrors the Firestore merge: a `null` deletes only that key, and an empty result
        // deletes the whole override.
        const current = await readNetOverrideFlat(data.userId);
        const next = { ...current };
        if (data.value === null) {
          delete next[data.permission];
        } else {
          next[data.permission] = data.value;
        }

        await writeNetOverride(data.userId, next);
      },
      onSuccess: (_data, variables) => {
        const targetCompanyId = variables.companyId || selectedCompany?.id;
        // Invalidate both the specific user's overrides and the list of all overrides
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.USER_PERMISSION_OVERRIDE, targetCompanyId, variables.userId],
        });
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.USER_PERMISSION_OVERRIDES, targetCompanyId],
        });
        notifications?.onSuccess?.('Brugertilladelse opdateret');
      },
    });
  };

  /**
   * Set multiple permission overrides for a user at once.
   * Useful for category-level "master switches".
   */
  const useSetMultipleUserPermissionOverrides = () => {
    return useMutation({
      mutationFn: async (data: {
        userId: string;
        permissions: Partial<Record<PermissionKey, boolean | null>>; // null to remove
        companyId?: string;
      }): Promise<void> => {
        const targetCompanyId = data.companyId || selectedCompany?.id;

        if (!targetCompanyId) {
          notifications?.onError?.('Ingen virksomhed valgt');
          return;
        }

        // .NET path: read-modify-write applying every key in `data.permissions` (null removes
        // that key), then delete-if-empty or PUT the granted/revoked split.
        const current = await readNetOverrideFlat(data.userId);
        const next = { ...current };
        for (const [key, value] of Object.entries(data.permissions)) {
          if (value === null) {
            delete next[key as PermissionKey];
          } else {
            next[key as PermissionKey] = value;
          }
        }

        await writeNetOverride(data.userId, next);
      },
      onSuccess: (_data, variables) => {
        const targetCompanyId = variables.companyId || selectedCompany?.id;
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.USER_PERMISSION_OVERRIDE, targetCompanyId, variables.userId],
        });
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.USER_PERMISSION_OVERRIDES, targetCompanyId],
        });
        notifications?.onSuccess?.('Brugertilladelser opdateret');
      },
    });
  };

  /**
   * Clear all permission overrides for a user.
   * Deletes the entire document.
   */
  const useClearUserPermissionOverrides = () => {
    return useMutation({
      mutationFn: async (data: {
        userId: string;
        companyId?: string;
      }): Promise<void> => {
        const targetCompanyId = data.companyId || selectedCompany?.id;

        if (!targetCompanyId) {
          notifications?.onError?.('Ingen virksomhed valgt');
          return;
        }

        // .NET path: DELETE the override row. A 404 (nothing to delete) is treated as success,
        // mirroring Firestore `deleteDoc` on a non-existent doc.
        const res = await requireApi().permissions.deleteUserOverride(data.userId);
        if (!res.ok && res.error?.status !== 404) {
          throw new Error(res.error?.detail ?? res.error?.title ?? 'Kunne ikke fjerne brugertilpasninger');
        }
      },
      onSuccess: (_data, variables) => {
        const targetCompanyId = variables.companyId || selectedCompany?.id;
        // Invalidate both the specific user's overrides and the list of all overrides
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.USER_PERMISSION_OVERRIDE, targetCompanyId, variables.userId],
        });
        queryClient.invalidateQueries({
          queryKey: [CACHE_KEYS.USER_PERMISSION_OVERRIDES, targetCompanyId],
        });
        notifications?.onSuccess?.('Brugertilpasninger fjernet');
      },
    });
  };

  return {
    useFetchUserPermissionOverride,
    useSetUserPermissionOverride,
    useSetMultipleUserPermissionOverrides,
    useClearUserPermissionOverrides,
  };
}
